import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { Avatar, AvatarGroup } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { ReactElement } from 'react';

import { avatarTone, tones } from './dashboardTokens';
import type { BatchAccount } from './types';

const MASKED_LABEL = /^Account\b/;
const INITIAL_COUNT = 2;

/** The first letter of the first two word-parts: `omar.h` gives `OH`. */
export const initialsOf = (label: string): string =>
  label
    .split(/[\s._-]+/)
    .map((part) => /^\p{L}/u.exec(part)?.[0].toUpperCase() ?? '')
    .filter(Boolean)
    .slice(0, INITIAL_COUNT)
    .join('');

export interface AccountAvatarProps {
  account: Pick<BatchAccount, 'accountId' | 'label'>;
  size?: number;
}

/** Decorative: the label next to it carries the name. A masked `Account …` label has no initials, so it shows a person. */
export default function AccountAvatar({ account, size = 32 }: AccountAvatarProps): ReactElement {
  const { bg, ink } = tones[avatarTone(account.accountId)];
  const initials = MASKED_LABEL.test(account.label) ? '' : initialsOf(account.label);
  return (
    <Avatar aria-hidden sx={{ width: size, height: size, bgcolor: bg, color: ink, fontSize: size * 0.375, fontWeight: 600 }}>
      {initials || <PersonOutlineOutlinedIcon sx={{ fontSize: size * 0.56 }} />}
    </Avatar>
  );
}

const GROUP_SIZE = 30;
const GROUP_SX: SxProps<Theme> = {
  '& .MuiAvatar-root': {
    width: GROUP_SIZE,
    height: GROUP_SIZE,
    fontSize: 11,
    fontWeight: 600,
    border: '2px solid',
    borderColor: 'white',
    boxSizing: 'border-box'
  }
};
const SURPLUS_SX: SxProps<Theme> = { bgcolor: 'neutral100', color: 'neutral800' };

/** Overlapping avatars with a "+N" for the rest, `max` counting that "+N". */
export function AccountAvatarGroup({ accounts, max }: { accounts: BatchAccount[]; max: number }): ReactElement {
  return (
    <AvatarGroup
      // The polymorphic default overflows the compiler (TS2590).
      component="div"
      aria-hidden
      max={max}
      slotProps={{ surplus: { sx: SURPLUS_SX } }}
      sx={GROUP_SX}
    >
      {accounts.map((account) => (
        <AccountAvatar key={account.accountId} account={account} size={GROUP_SIZE} />
      ))}
    </AvatarGroup>
  );
}
